/**
 * Shared OCC lens chrome (S4.3): the per-lens footer (資料同步於 {syncedAt} ·
 * NetSuite Tier-1 note — every lens renders one), the read-only banner for
 * referenced/read-only lenses, and the severity tag.
 */

import { Tag, Typography, theme } from 'antd';
import { EyeOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { OccExceptionSeverity } from '@/lib/occ/selectors';

const { Text } = Typography;

/** The lens data domains a degraded state can name (operator vocabulary). */
export type OccLensDomain =
  | 'overview'
  | 'lines'
  | 'vendorConfirm'
  | 'exceptions'
  | 'batches'
  | 'invoiceStatus'
  | 'receiptStatus';

/** Static key map (not a template literal) keeps the i18n audit exact. */
const LENS_UNAVAILABLE_KEY: Record<OccLensDomain, string> = {
  overview: 'occ.lens.unavailable.overview',
  lines: 'occ.lens.unavailable.lines',
  vendorConfirm: 'occ.lens.unavailable.vendorConfirm',
  exceptions: 'occ.lens.unavailable.exceptions',
  batches: 'occ.lens.unavailable.batches',
  invoiceStatus: 'occ.lens.unavailable.invoiceStatus',
  receiptStatus: 'occ.lens.unavailable.receiptStatus',
};

/**
 * F2.3 degraded-state indicator (D-CAT-17 pattern surfaced to the operator).
 *
 * FAIL-OPEN: renders NOTHING unless the lens read actually failed with a
 * missing route — a healthy page shows zero degradation chrome. The copy
 * names the affected DATA DOMAIN in operator vocabulary (zh+en), never the
 * word "lens" or a route name (`occ.lens.*` is only the i18n KEY namespace).
 * Icon + one-line text through design tokens — never a colour-only signal.
 */
export const LensUnavailableNotice = ({
  active,
  domain,
}: {
  active: boolean;
  domain: OccLensDomain;
}) => {
  const { t } = useTranslation('occ');
  const { token } = theme.useToken();

  if (!active) return null;

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: token.marginXS,
        padding: `${token.paddingXS}px ${token.padding}px`,
        marginBottom: token.marginMD,
        borderRadius: token.borderRadius,
        backgroundColor: token.colorWarningBg,
        border: `1px solid ${token.colorWarningBorder}`,
      }}
    >
      <ExclamationCircleOutlined
        style={{ color: token.colorWarning, fontSize: token.fontSizeLG }}
      />
      <Text style={{ fontSize: token.fontSizeSM, color: token.colorText }}>
        {t(LENS_UNAVAILABLE_KEY[domain])}
      </Text>
    </div>
  );
};

/** Every lens footer: 資料同步於 {syncedAt} · NetSuite Tier-1 note. */
export const LensFooter = ({ syncedAt }: { syncedAt?: string }) => {
  const { t } = useTranslation('occ');
  const { token } = theme.useToken();

  return (
    <div
      style={{
        marginTop: token.marginMD,
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: token.marginSM,
        color: token.colorTextTertiary,
        fontSize: token.fontSizeSM,
      }}
    >
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        {t('occ.footer.syncedAt', {
          time: syncedAt ? dayjs(syncedAt).format('YYYY-MM-DD HH:mm') : '-',
        })}
      </span>
      <span>·</span>
      <span>{t('occ.footer.tierNote')}</span>
    </div>
  );
};

/** Read-only banner for referenced / read-only lenses (visibly not owned). */
export const ReadOnlyBanner = ({ note }: { note: string }) => {
  const { t } = useTranslation('occ');
  const { token } = theme.useToken();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: token.marginXS,
        marginBottom: token.marginMD,
      }}
    >
      <Tag
        icon={<EyeOutlined />}
        style={{
          margin: 0,
          backgroundColor: token.colorFillQuaternary,
          color: token.colorTextSecondary,
          borderColor: token.colorBorderSecondary,
        }}
      >
        {t('occ.readOnly.badge')}
      </Tag>
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        {note}
      </Text>
    </div>
  );
};

const SEVERITY_TAG_COLOR: Record<OccExceptionSeverity, string> = {
  high: 'red',
  medium: 'orange',
  low: 'default',
};

/** Static key map (not a template literal) keeps the i18n audit exact. */
const SEVERITY_LABEL_KEY: Record<OccExceptionSeverity, string> = {
  high: 'occ.severity.high',
  medium: 'occ.severity.medium',
  low: 'occ.severity.low',
};

/** Severity tag — colour + localized label (label is the non-colour channel). */
export const SeverityTag = ({ severity }: { severity: OccExceptionSeverity }) => {
  const { t } = useTranslation('occ');
  return (
    <Tag color={SEVERITY_TAG_COLOR[severity]} style={{ margin: 0 }}>
      {t(SEVERITY_LABEL_KEY[severity])}
    </Tag>
  );
};
